import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { LucideStar } from "lucide-react";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { forwardRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  hotelCensusLocationType,
  iCollection,
  locationTypes,
  patchCollection,
} from "@/services/collection";
import MapsAutocomplete from "@/components/mapsautocomplete/MapsAutocomplete";
import { useModal } from "@/context/ModalContext";
import { LoadingSubmitButton } from "@/components/form_fields/LoadingSubmitButton";
import { useRbac } from "@/hooks/rbac";

const DetailsForm = forwardRef(function Details({
  collection,
  nextPage,
  onRefresh,
}: {
  collection: iCollection;
  nextPage?: string;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperAdmin } = useRbac();
  const formSchema = z.object({
    name: z.string(),
    stars: z.string(),
    tags: z.enum(locationTypes).array(),
    locationField: z.string(),
    email: z.string().email().optional(),
    website: z.string().optional(),
    phone: z.string().optional(),
    payment: z.boolean(),
    cin: z.string().optional(),
    cir: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const [isLoading, setIsLoading] = useState(false);

  const [censusLocation, setCensusLocation] =
    useState<hotelCensusLocationType | null>(null);
  const { hideModal } = useModal();

  const handleAddressSelection = (selectedAddress: any) => {
    if (!selectedAddress) {
      form.resetField("locationField");
    } else {
      setCensusLocation(selectedAddress);
      form.setValue("locationField", selectedAddress.label);
      form.clearErrors("locationField");
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      // pick dei fields da gestire manualmente
      const { name, stars, tags, email, payment, cin, cir, website } = data;

      await patchCollection({
        checkBeforeSale: !payment,
        name,
        locationType: tags[0] ? tags[0] : undefined,
        cin,
        cir,
        email,
        stars: stars == "na" ? "" : stars,
        website: website,
        collectionId: collection._id,
        tags: tags,
        census: {
          location: censusLocation,
        },
      });
      await onRefresh();
      hideModal();
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
    if (nextPage && !isLoading) {
      navigate(nextPage);
    }
  };

  useEffect(() => {
    const { location } = collection.census || {};

    // prefill form with collection data
    const formFromCollection = {
      email: collection.email,
      website: collection.website,
      cin: collection.cin,
      cir: collection.cir,
      phone: collection.phone,
      name: collection.name,
      stars: collection.stars || "na",
      locationField: location?.label,
      payment: !collection.checkBeforeSale,
      tags: collection.tags || [],
    };

    form.reset(formFromCollection);
    setCensusLocation(location);
  }, [collection]);

  return collection.type === "experience" ? null : (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("collection.census_name")} {t("form.required")}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t("collection.census_name_placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="stars"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("collection.census_stars")} {t("form.required")}
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("collection.census_stars_placeholder")}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem key={0} value="na">
                      N/A
                    </SelectItem>
                    {[1, 2, 3, 4, 5, 6].map((value) => (
                      <SelectItem key={value} value={String(value)}>
                        <div className="flex items-center">
                          {value === 6 ? (
                            <>
                              {[...Array(5)].map((_, i) => (
                                <LucideStar key={i} className="w-3 h-3" />
                              ))}
                              <span className="ml-2">L</span>
                            </>
                          ) : (
                            [...Array(value)].map((_, i) => (
                              <LucideStar key={i} className="w-3 h-3" />
                            ))
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {isSuperAdmin ? (
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags</FormLabel>

                <div className="flex flex-wrap gap-2">
                  {field.value
                    ? locationTypes.map((tag) => {
                        const isSelected = field.value.includes(tag);
                        return (
                          <button
                            key={tag}
                            className="flex items-center gap-2 px-2 border-gray-200 border-[1px] rounded-xl"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isSelected) {
                                field.onChange(
                                  field.value.filter((v) => v !== tag)
                                );
                                return;
                              }
                              field.onChange(field.value.concat(tag));
                            }}
                            style={{
                              backgroundColor: isSelected
                                ? "#e5e7eb"
                                : "transparent",
                            }}
                          >
                            {tag}
                          </button>
                        );
                      })
                    : null}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <FormField
          control={form.control}
          name="locationField"
          render={() => (
            <FormItem>
              <FormLabel>
                {t("collection.census_location")} {t("form.required")}
              </FormLabel>
              <FormControl>
                <MapsAutocomplete
                  defaultValue={censusLocation}
                  onSelect={handleAddressSelection}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("collection.public_website")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("collection.census_website_placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("collection.census_email_placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("collection.public_phone")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("collection.census_phone_placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="payment"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>
                {t("collection.census_payment")} {t("form.required")}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value?.toString()}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value={true as any} />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("collection.census_yes")}
                      {", "}
                      {t("collection.census_yes_description")}
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value={false as any} />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("collection.census_no")}
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormDescription>
                {t("collection.census_payment_tip")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cin"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("collection.cin")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("collection.cin_placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="cir"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("collection.cir")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("collection.cir_placeholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="border-b h-8 mb-8"></div>
        <div className="flex flex-row gap-4">
          <LoadingSubmitButton
            className="ml-auto"
            variant="takyon"
            type="submit"
            isLoading={isLoading}
          >
            {t("buttons.save_and_continue")}
          </LoadingSubmitButton>
        </div>
      </form>
    </Form>
  );
});

export default DetailsForm;
